import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { LineItem } from '@models/line-item.model';
import { Product } from '@models/product.model';
import { PurchaseOrderDto } from '@app/models/purchase-order.model.dto';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CompanyService } from '@services/company.service';
import { ModalPOFilesComponent } from '@shared/modals';
import { Toaster } from '@services/index';
import { LocalStorage } from 'ngx-webstorage';
import { from } from 'rxjs';

@Component({
  selector: 'ngx-line-items-list',
  templateUrl: './line-items-list.component.html',
  styleUrls: ['./line-items-list.component.scss'],
})
export class LineItemsListComponent implements OnInit {

  @Input() companyType: string;
  @Input() allowEdit: boolean = true;
  @Input() allowDelete: boolean = true;
  @Input() products: Product[];
  @Input() purchaseOrder: PurchaseOrderDto;
  @LocalStorage() organizationId: string;

  private _lineItems: LineItem[];
  @Input()
  set lineItems(value) {
    this._lineItems = value;
    this.updateRows();
  }
  get lineItems(): LineItem[] {
    return this._lineItems;
  }

  rows: any;

  @Output() removeLineItem: EventEmitter<{ lineItem: LineItem, index: number }> = new EventEmitter();
  @Output() updateLineItem: EventEmitter<{ lineItem: LineItem }> = new EventEmitter();

  constructor(
    private companyService: CompanyService,
    private $modal: NgbModal,
    private router: Router,
    private $toaster: Toaster,
  ) { }

  ngOnInit() { 
  }

  updateRows() {
    this.rows = [...this.lineItems];
    from(this.lineItems).subscribe(li => {
      if (li.id) {
        this.getSteps(li);
      } else if (li.product_id) {
        li.id = li.product_id;
      }
    });
  }

  openModalWithAttachments(lineItem) {
    this.companyService.fetchProduct(this.purchaseOrder.supplier_id, lineItem.product_id).subscribe(data => {
      let files: any;
      if (data.files && data.files.length) {
        files = data.files;
      }
      const modalFiles = this.$modal.open(ModalPOFilesComponent, {
        size: 'lg',
        container: 'nb-layout',
        backdrop: 'static',
      });
      modalFiles.componentInstance.files = data.files;
      modalFiles.componentInstance.purchaseOrderId = this.purchaseOrder.id;
    });
  }

  displayProductName(productId: string): string {
    const prod = this.products.find(p => p.id === productId);
    return !!prod ? prod.name.charAt(0).toUpperCase() + prod.name.slice(1) : productId;
  }

  getProductLink(li) {
    const section = this.companyType === 'customer' ? '/products/products-buy' : '/products/products-sell';
    return this.router.createUrlTree([section, li.product_id]).toString();
  }

  removeItem($event, li) {
    this.removeLineItem.emit({ lineItem: li, index: this.lineItems.indexOf(li) });
  }

  updateItem($event, li, isStep: boolean) {
    if (isStep) {
      this.setStepProgress($event, li.parent_id, li);
    } else {
      li.quantity_ordered = parseInt($event.srcElement.value);
      this.updateLineItem.emit({ lineItem: li });
    }
  }

  get canDelete(): boolean {
    return this.companyType === 'customer' && this.allowDelete;
  }

  get canEdit(): boolean {
    return this.companyType === 'customer' && this.allowEdit;
  }

  canEditQtyOrdered(item): boolean {
    return this.companyType === 'customer' && this.allowEdit && !item.parent_id;
  }

  canEditQtyCompleted(item): boolean {
    return this.companyType === 'supplier' && !!item.parent_id;
  }

  async getSteps(item) {
    if (!item.id) {
      return;
    }
    this.companyService.lineItemSteps(this.organizationId, item.id)
      .subscribe((steps: any) => {
        item.steps = steps.slice().sort((a, b) => a.sequence_number - b.sequence_number);
        from(steps).subscribe(step => {
          let s: any = (step as any);
          this.rows = [...this.rows, {
            id: s.id, 
            parent_id: item.id, 
            product_name: 'Step: ' + s.name, 
            barcode: s.barcode, 
            quantity_ordered: item.quantity_ordered,
            quantity_completed: s.quantity,
            description: s.description,
          }];
        });
      });
  }

  setStepProgress($event, lineItemID: string, step) {
    let qty: number;
    try {
      const parsed = parseInt($event.srcElement.value);
      if (isNaN(parsed)) {
        throw new Error("provided value is not a number");
      }
      if (parsed < 0) {
        throw new Error("negative values are not allowed");
      }
      if (parsed > step.quantity_ordered) {
        throw new Error("you cannot set a value greater than the quantity ordered");
      }
      qty = parsed;
    } catch (err) {
      console.log(err);
      $event.srcElement.value = step.quantity_completed;
      this.$toaster.show('error', 'Error', 'Please provide a valid value: ' + err.message);
      return;
    }

    this.companyService.updateStepProgress(lineItemID, step.id, qty)
      .subscribe(res => {
        $event.srcElement.value = qty;
        step.quantity_completed = qty;
        this.$toaster.show('success', 'Success', 'You changes were saved.');
      }, error => {
        console.log(error);
        this.$toaster.show('error', 'Error', 'It was not possible to update the quantity for this step, please try again later.');
      });
  }
}
